import { useEffect } from 'react';
import type { ReactNode, MouseEvent } from 'react';
import { cn } from '../../lib/utils.js';

/**
 * Shared modal shell for the hand-rolled centered dialogs.
 *
 * Renders in place (no portal) so overlays nested inside other overlays —
 * e.g. AddRecipientModal opened from the notifications DraftDrawer — keep
 * stacking by DOM order, exactly like the markup this replaces.
 *
 * Below `sm` the panel is a bottom sheet (full width, capped at 92dvh with
 * safe-area padding); at `sm+` it is the familiar centered dialog. The panel
 * is always a column: `ModalHeader`/`ModalFooter` stay fixed while
 * `ModalBody` scrolls, so tall content can never push the actions
 * off-screen.
 *
 * Backdrop click does NOT close by default — none of the dialogs this
 * replaces closed on backdrop, and several hold unsaved edits. Opt in with
 * `closeOnBackdrop`. Escape closes when `canClose` allows (pass the same
 * condition that guards the ✕ button, e.g. `!isSubmitting`).
 */

const SIZE_CLASSES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
} as const;

export interface ModalProps {
  onClose: () => void;
  /** Gates Escape, backdrop close and nothing else — keep the ✕ button's own
   *  disabled state in sync at the call site. Default true. */
  canClose?: boolean;
  /** Close when the backdrop itself is clicked. Default false. */
  closeOnBackdrop?: boolean;
  size?: keyof typeof SIZE_CLASSES;
  /** Extra classes for the panel (not the backdrop). */
  className?: string;
  children: ReactNode;
}

export function Modal({
  onClose,
  canClose = true,
  closeOnBackdrop = false,
  size = 'lg',
  className,
  children,
}: ModalProps) {
  useEffect(() => {
    if (!canClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [canClose, onClose]);

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdrop && canClose && e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={handleBackdropClick}
    >
      <div
        className={cn(
          'bg-surface shadow-2xl w-full flex flex-col overscroll-contain',
          'rounded-t-2xl max-h-[92dvh] pb-[env(safe-area-inset-bottom)]',
          'sm:rounded-xl sm:max-h-[85vh] sm:pb-0',
          SIZE_CLASSES[size],
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function ModalHeader({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex-shrink-0 flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-grey-200',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ModalBody({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6', className)}>
      {children}
    </div>
  );
}

export function ModalFooter({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex-shrink-0 flex items-center justify-end gap-3 flex-wrap p-4 sm:p-6 border-t border-grey-200',
        className,
      )}
    >
      {children}
    </div>
  );
}
